import type {
    ComposableOptionsBase,
    SalesTransaction,
    SiteMembershipContext
} from '~/types';
import type {
    PaginatedPayload,
    CustomPagination,
    PaginatedRequest
} from 'x1-base/types';
import {
    SalesTransactionType,
} from '~/types';
import {
    useCustomFetch,
    orderExpanders
} from '~/composables';
import { useApiUtils } from 'x1-base/composables';
import { omit as lo_omit } from 'es-toolkit';
import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { pathJoin } from 'x1-base/utils';
import type { NitroFetchOptions } from 'nitropack';

export interface UseSalesTransactionOptions extends ComposableOptionsBase {
    type: Nullable<SalesTransactionType>;
    siteMembershipContext?: SiteMembershipContext;
}
export function useSalesTransaction(options: Partial<UseSalesTransactionOptions> = {}) {
    const tnxType = options.type ? getEndpointFromType(options.type) : null;

    if (!tnxType) {
        console.warn('useSalesTransaction: Invalid type provided.');
    }

    const apiBasePath = `${tnxType ?? ''}`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch();

    async function getItems<T = SalesTransaction>(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {}
    ): Promise<PaginatedPayload<T> | Error> {
        const url = args?.url ?? `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {}
        };

        lo_mergeCompat(config.query, args?.params, query, {
            // Base params here
        });

        let paginatedPayload: PaginatedPayload<T>;

        try {
            const response = await $_fetch<CustomPagination<T>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getItem(uid: string): Promise<SalesTransaction | Error> {
        const url = `${apiBasePath}/${uid}/`;
        const expanders = [...orderExpanders];

        // `applied_to` (what the credit is currently applied to) only exists on credit
        // memos and runs a per-row ledger query, so only request it for that type.
        if (options.type === SalesTransactionType.CREDIT_MEMO) {
            expanders.push('applied_to');
        }

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {
                expand: expanders.join(',')
            }
        };

        try {
            return await $_fetch<SalesTransaction>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    function getOrderReturnLabel(uid: string): string {
        const baseUrl = useApiUtilsObj.getApiBaseUrl('cms') || '';
        return pathJoin(baseUrl, `/orders/return-label/${uid}/`);
    }

    // Internal

    function getEndpointFromType(type: SalesTransactionType): string {
        switch (type) {
            case SalesTransactionType.ORDER:
                return `/v1/orders`;
            case SalesTransactionType.INVOICE:
                return `/v1/invoices`;
            case SalesTransactionType.CREDIT_MEMO:
                return `/v1/credit-memos`;
            default:
                return '';
        }
    }

    return {
        getItems,
        getItem,
        getOrderReturnLabel
    };
}
